import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type { MfaEnrollResponse, MfaFactors, WebAuthnCredentialInfo } from "../../api/types";
import { useAccount, useAuth } from "../../auth/AuthProvider";
import { CommandError } from "../../components/CommandError";
import { QrCode } from "../../components/QrCode";
import {
  Banner,
  Button,
  Card,
  Checkbox,
  CopyField,
  Dialog,
  EmptyState,
  Input,
  PageHeader,
  QueryView,
  StatusPill,
  Table,
  useToast,
} from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { createCredential, WebAuthnCancelledError, webAuthnSupported } from "../../lib/webauthn";

// UX-DESIGN.md §2.1 step 3 / §3.0 / §4.1: MFA enrollment is required before
// any wallet or payout screen. Two factor types (internal/auth):
//   TOTP     — POST /v1/auth/mfa/enroll then /confirm (returns recovery codes once)
//   WebAuthn — POST /v1/auth/mfa/webauthn/register/begin|finish; list; remove
// Adding a second factor, and removing any, needs fresh step-up; the API
// client's interceptor handles that prompt.
//
// What is enrolled comes from GET /v1/auth/mfa/factors (G-05,
// cmd/core-platform/account.go:157-185): the TOTP confirmation date, the
// keys, and how many recovery codes are left. POST
// /v1/auth/mfa/recovery-codes (RequireRecentMFA) issues a new batch and
// voids the old one; it isn't replayable, so a retry is another batch.

export function SecurityPage() {
  const session = useAccount();
  const ep = useEndpoints();
  const [params] = useSearchParams();
  const setupMode = params.get("setup") === "1" || session.mfa === "not_enrolled";
  const factors = useQuery({ queryKey: ["mfa-factors"], queryFn: () => ep.auth.mfaFactors() });

  return (
    <div className="stack-lg">
      <PageHeader
        title="Sign-in & security"
        description="Two-step verification protects every financial action on this account."
      />
      {setupMode && session.mfa !== "enrolled" ? (
        <Banner tone="warning" title="Set up two-step verification to continue">
          {session.partyType === "advertiser"
            ? "Your wallet stays locked until two-step verification is set up — adding funds, refunds and spend changes all require it."
            : session.partyType === "platform"
              ? "Staff decisions that move money or change what's served ask for a fresh verification, which needs a factor first."
              : "Payout setup and payout requests stay locked until two-step verification is set up."}{" "}
          A security key or passkey is the strongest option; an authenticator app works everywhere.
        </Banner>
      ) : null}
      <SecurityKeysCard />
      <TotpCard factors={factors} />
      <RecoveryCodesCard factors={factors} />
    </div>
  );
}

function RecoveryCodesDisplay({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [ack, setAck] = useState(false);
  function download() {
    const blob = new Blob([`VADS recovery codes\nEach code works once.\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vads-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="stack">
      <Banner tone="warning" title="Save these recovery codes now — you won't see them again">
        Each code signs you in once if you lose your device. Store them somewhere safe and separate from your device.
      </Banner>
      <ul className="recovery-codes" aria-label="Recovery codes">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="row">
        <Button variant="secondary" onClick={download}>
          Download as text file
        </Button>
        <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(codes.join("\n"))}>
          Copy all
        </Button>
      </div>
      <Checkbox label="I've saved my recovery codes somewhere safe" checked={ack} onChange={(e) => setAck(e.target.checked)} />
      <div>
        <Button onClick={onDone} disabled={!ack}>
          Done
        </Button>
      </div>
    </div>
  );
}

function SecurityKeysCard() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const { markMfaEnrolled, refreshMe } = useAuth();
  const toast = useToast();
  const [label, setLabel] = useState("");
  const [labelError, setLabelError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [removing, setRemoving] = useState<WebAuthnCredentialInfo | null>(null);

  const list = useQuery({ queryKey: ["webauthn-credentials"], queryFn: () => ep.auth.webauthn.list() });

  const add = useMutation({
    mutationFn: async (name: string) => {
      const { options } = await ep.auth.webauthn.registerBegin(name);
      const credential = await createCredential(options);
      return ep.auth.webauthn.registerFinish(credential);
    },
    onSuccess: (res) => {
      setLabel("");
      markMfaEnrolled();
      void qc.invalidateQueries({ queryKey: ["webauthn-credentials"] });
      void qc.invalidateQueries({ queryKey: ["mfa-factors"] });
      if (res.recovery_codes?.length) setCodes(res.recovery_codes);
      else toast.show("Security key added.", "success");
    },
    onError: (err) => {
      if (err instanceof WebAuthnCancelledError) setError(err.message);
      else setError(err instanceof Error ? err.message : "Couldn't add the key.");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => ep.auth.webauthn.remove(id),
    onSuccess: () => {
      setRemoving(null);
      toast.show("Security key removed.", "success");
      void qc.invalidateQueries({ queryKey: ["webauthn-credentials"] });
      void qc.invalidateQueries({ queryKey: ["mfa-factors"] });
      void refreshMe();
    },
    onError: (err) => {
      setRemoving(null);
      setError(err instanceof Error ? err.message : "Couldn't remove the key.");
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed || trimmed.length > 64) {
      setLabelError("Give the key a name of 1–64 characters, like “YubiKey (desk)”.");
      return;
    }
    setLabelError(null);
    setError(null);
    add.mutate(trimmed);
  }

  if (codes) {
    return (
      <Card title="Security keys & passkeys">
        <RecoveryCodesDisplay
          codes={codes}
          onDone={() => {
            setCodes(null);
            toast.show("Two-step verification is on.", "success");
          }}
        />
      </Card>
    );
  }

  return (
    <Card
      title="Security keys & passkeys"
      subtitle="Phishing-resistant, and preferred for confirming payouts, refunds and destination changes. Keys must support a PIN or biometric."
    >
      <div className="stack">
        {error ? (
          <Banner tone="error" live="alert">
            {error}
          </Banner>
        ) : null}
        <QueryView query={list} loadingLabel="Loading security keys">
          {(data) =>
            data.credentials.length === 0 ? (
              <EmptyState icon="key" title="No security keys yet" />
            ) : (
              <Table
                caption="Registered security keys"
                captionHidden
                rows={data.credentials}
                rowKey={(c) => c.id}
                columns={[
                  { key: "label", header: "Name", cell: (c) => c.label },
                  { key: "added", header: "Added", cell: (c) => formatLocal(c.created_at) },
                  { key: "used", header: "Last used", cell: (c) => (c.last_used_at ? formatLocal(c.last_used_at) : "Never") },
                  {
                    key: "status",
                    header: "Status",
                    cell: (c) =>
                      c.clone_warning_at ? (
                        <StatusPill tone="error">Possibly cloned — blocked</StatusPill>
                      ) : (
                        <StatusPill tone="success">Active</StatusPill>
                      ),
                  },
                  {
                    key: "actions",
                    header: <span className="visually-hidden">Actions</span>,
                    cell: (c) => (
                      <Button variant="ghost" size="sm" onClick={() => setRemoving(c)}>
                        Remove <span className="visually-hidden">{c.label}</span>
                      </Button>
                    ),
                  },
                ]}
              />
            )
          }
        </QueryView>
        {webAuthnSupported() ? (
          <form className="stack-sm" onSubmit={submit} noValidate>
            <Input
              label="Name for a new key"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              error={labelError}
              maxLength={64}
              hint="Helps you tell your keys apart later."
            />
            <div>
              <Button type="submit" loading={add.isPending}>
                Add security key
              </Button>
            </div>
          </form>
        ) : (
          <Banner tone="neutral">This browser doesn't support security keys. Use an authenticator app, or switch browsers.</Banner>
        )}
      </div>
      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        alert
        title={`Remove “${removing?.label ?? ""}”?`}
        description="You'll confirm it's you first. You can't remove your only verification method."
        actions={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => removing && remove.mutate(removing.id)}>
              Remove key
            </Button>
          </>
        }
      />
    </Card>
  );
}

function TotpCard({ factors }: { factors: UseQueryResult<MfaFactors> }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const { markMfaEnrolled } = useAuth();
  const { me } = useAccount();
  const toast = useToast();
  const [label, setLabel] = useState("");
  const [enrollment, setEnrollment] = useState<MfaEnrollResponse | null>(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [alreadyOn, setAlreadyOn] = useState(false);
  const confirmedAt = factors.data?.totp?.confirmed_at ?? null;

  const enroll = useMutation({
    mutationFn: () => ep.auth.enrollTotp(label.trim() || me.email),
    onSuccess: (res) => {
      setEnrollment(res);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "auth: mfa already confirmed") setAlreadyOn(true);
      else setError(err instanceof Error ? err.message : "Couldn't start setup.");
    },
  });

  const confirm = useMutation({
    mutationFn: (c: string) => ep.auth.confirmTotp(c),
    onSuccess: (res) => {
      markMfaEnrolled();
      setCodes(res.recovery_codes);
      setEnrollment(null);
      void qc.invalidateQueries({ queryKey: ["mfa-factors"] });
    },
    onError: (err) => setCodeError(err instanceof Error ? err.message : "That code didn't work."),
  });

  function submitCode(e: FormEvent) {
    e.preventDefault();
    const v = code.replace(/\s/g, "");
    if (!/^\d{6}$/.test(v)) {
      setCodeError("Enter the 6-digit code your app shows now.");
      return;
    }
    setCodeError(null);
    confirm.mutate(v);
  }

  if (codes) {
    return (
      <Card title="Authenticator app">
        <RecoveryCodesDisplay
          codes={codes}
          onDone={() => {
            setCodes(null);
            setAlreadyOn(true);
            toast.show("Authenticator app is set up.", "success");
          }}
        />
      </Card>
    );
  }

  return (
    <Card title="Authenticator app" subtitle="Time-based 6-digit codes from an app such as 1Password, Authy or Google Authenticator.">
      {alreadyOn || confirmedAt ? (
        <div className="stack-sm">
          <span>
            <StatusPill tone="success">An authenticator app is set up</StatusPill>
          </span>
          {confirmedAt ? <span className="small muted">Turned on {formatLocal(confirmedAt)}.</span> : null}
        </div>
      ) : enrollment ? (
        <div className="stack">
          <p className="small">In your authenticator app, add an account and scan this QR code:</p>
          <QrCode value={enrollment.provisioning_uri} label="QR code for adding this account to your authenticator app" />
          <p className="small">
            Can't scan it? Enter this setup key instead, or open the link on a device that has the app:
          </p>
          <CopyField label="Setup key" value={enrollment.secret} />
          <p className="small">
            <a href={enrollment.provisioning_uri}>Open in authenticator app</a>
          </p>
          <form className="stack-sm" onSubmit={submitCode} noValidate>
            <Input
              label="Enter the 6-digit code your app shows"
              inputMode="numeric"
              autoComplete="one-time-code"
              mono
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={codeError}
              maxLength={7}
            />
            <div className="row">
              <Button type="submit" loading={confirm.isPending}>
                Confirm and turn on
              </Button>
              <Button variant="ghost" onClick={() => setEnrollment(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="stack">
          {error ? (
            <Banner tone="error" live="alert">
              {error}
            </Banner>
          ) : null}
          <Input
            label="Account label shown in your app"
            hint={`Leave empty to use your sign-in email, ${me.email}.`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            optional
          />
          <div>
            <Button variant="secondary" onClick={() => enroll.mutate()} loading={enroll.isPending}>
              Set up authenticator app
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function RecoveryCodesCard({ factors }: { factors: UseQueryResult<MfaFactors> }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const { refreshMe } = useAuth();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const regenerate = useMutation({
    mutationFn: () => ep.auth.regenerateRecoveryCodes(),
    onSuccess: (res) => {
      setConfirming(false);
      setCodes(res.recovery_codes);
      void qc.invalidateQueries({ queryKey: ["mfa-factors"] });
      void refreshMe();
    },
    onError: () => setConfirming(false),
  });

  if (codes) {
    return (
      <Card title="Recovery codes" level={2}>
        <RecoveryCodesDisplay
          codes={codes}
          onDone={() => {
            setCodes(null);
            regenerate.reset();
            toast.show("New recovery codes saved. The old ones no longer work.", "success");
          }}
        />
      </Card>
    );
  }

  return (
    <Card
      title="Recovery codes"
      level={2}
      subtitle="One-time codes for the sign-in verification step if you lose your device. They can't be used to confirm payments."
    >
      <QueryView query={factors} loadingLabel="Loading your verification methods">
        {(f) =>
          !f.totp && f.webauthn.length === 0 ? (
            <p className="small muted">You'll get ten recovery codes when you turn on your first verification method above.</p>
          ) : (
            <div className="stack">
              <p>
                <strong>{f.recovery_codes_remaining}</strong> unused recovery {f.recovery_codes_remaining === 1 ? "code" : "codes"} left.
              </p>
              {f.recovery_codes_remaining <= 3 ? (
                <Banner tone="warning" title={f.recovery_codes_remaining === 0 ? "No recovery codes left" : "Running low on recovery codes"}>
                  Generate a new set so you can still sign in if you lose your device.
                </Banner>
              ) : null}
              {regenerate.isError ? <CommandError error={regenerate.error} /> : null}
              <div>
                <Button variant="secondary" onClick={() => setConfirming(true)}>
                  Generate new recovery codes
                </Button>
              </div>
            </div>
          )
        }
      </QueryView>
      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        alert
        dismissible={!regenerate.isPending}
        title="Generate new recovery codes?"
        description="Every unused code you have now stops working, and you get ten new ones, shown once. You'll confirm it's you first."
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={regenerate.isPending}>
              Cancel
            </Button>
            <Button onClick={() => regenerate.mutate()} loading={regenerate.isPending}>
              Generate new codes
            </Button>
          </>
        }
      />
    </Card>
  );
}
